import { useMemo } from 'react';

import { EventMutationTradeProps } from '../event-mutation';
import { EventExpiration } from './event-expiration';
import EventMutationSetLimitPrice from './event-mutation-set-limit-price';
import { EventSellSharesInput } from './event-sell-shares-input';
import EventSellProfitMultiplierRow from './rows/event-sell-profit-multiplier-row';
import EventSellProfitRow from './rows/event-sell-profit-row';
import { EventSellReceive } from './rows/event-sell-receive';

import { ORDER_TYPE, PRICE_DECIMALS } from '@/constants/solana';
import { useFetchOrderbookState } from '@/hooks/solana/query/use-fetch-orderbook-state';
import { getMatchingAmount } from '@/lib/utils';
import { mapOrderbook } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';

export const EventMutationSellLimit = ({
  outcome,
  setOutcome,
  id,
  setShares,
  shares,
}: EventMutationTradeProps) => {
  const { price, setPrice } = useOrderbookStore();
  const { data: orderbook } = useFetchOrderbookState(Number(id));

  const orderbookData = useMemo(() => {
    return mapOrderbook(orderbook, outcome);
  }, [orderbook, outcome]);

  const { matchingAmount, averageLimitPrice, matchingCost, nextBestPrice } =
    useMemo(() => {
      if (!price || !shares) {
        const allOrders = orderbookData['bids']
          .filter((bid) => bid.price > 0)
          .sort((a, b) => b.price - a.price);

        return {
          matchingAmount: 0,
          averagePrice: 0,
          averageLimitPrice: 0,
          matchingCost: 0,
          nextBestPrice: allOrders[0]?.price || null,
        };
      }
      return getMatchingAmount(orderbookData, ORDER_TYPE.SELL, +price, +shares);
    }, [orderbookData, price, shares]);

  return (
    <>
      <EventMutationSetLimitPrice price={price} setPrice={setPrice} />
      <div className="border border-[#212121]" />

      <EventSellSharesInput
        id={id}
        outcome={outcome}
        shares={shares}
        setShares={setShares}
        matchingAmount={matchingAmount}
      />
      <EventExpiration />
      <div className="border border-[#212121]" />

      <div className="flex flex-col gap-2">
        {/* <EventAveragePriceRow averageLimitPrice={averageLimitPrice} /> */}
        <EventSellProfitRow
          price={price}
          shares={shares}
          id={id}
          outcome={outcome}
          averageOrderPrice={averageLimitPrice}
        />
        <EventSellProfitMultiplierRow
          price={price}
          shares={shares}
          id={id}
          outcome={outcome}
          averageOrderPrice={averageLimitPrice}
        />
      </div>
      <div className="border border-[#212121]" />

      <EventSellReceive
        marketId={id}
        receiveAmountWithoutFee={
          isNaN(+shares) || isNaN(+averageLimitPrice)
            ? 0
            : (averageLimitPrice * +shares) / PRICE_DECIMALS
        }
        price={price}
        nextBestPrice={nextBestPrice}
        matchingAmount={isNaN(matchingCost) ? 0 : matchingCost / PRICE_DECIMALS}
        tradeType="sell"
        avgPrice={averageLimitPrice}
      />
    </>
  );
};
